import { useEffect, useState } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from './useAuth';

/**
 * Returns a Set of date strings (YYYY-MM-DD) for which the authenticated user
 * has completed a game in any category during the given month.
 * Anonymous users always get an empty set.
 */
export const usePlayedDays = (
  year: number,
  month: number // 0-indexed (0 = January)
): { playedDates: Set<string>; loading: boolean } => {
  const { user } = useAuth();
  const [playedDates, setPlayedDates] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.id) {
      setPlayedDates(new Set());
      return;
    }

    let cancelled = false;
    setLoading(true);

    const startOfMonth = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    // Last day of month
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const endOfMonth = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    (async () => {
      try {
        const { data, error } = await supabase
          .from('game_plays')
          .select('game_date')
          .eq('user_id', user.id)
          .gte('game_date', startOfMonth)
          .lte('game_date', endOfMonth);

        if (!cancelled && !error && data) {
          // Deduplicate dates (user may have played multiple categories on same day)
          setPlayedDates(new Set(data.map((r) => r.game_date)));
        }
      } catch {
        // silently ignore – no highlights is fine
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, year, month]);

  return { playedDates, loading };
};
